import * as math from "mathjs";

/**
 * Type definition for GCP (Ground Control Point)
 */
export interface GCP {
  id: string;
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
}

/**
 * Helper function to calculate initial map view state from GCPs
 */
export const calculateInitialViewState = (gcps: GCP[]) => {
  if (!gcps.length) {
    return {
      longitude: 139.7,
      latitude: 35.7,
      zoom: 5,
    }; // Default value (near Tokyo)
  }

  // Calculate center point from GCP target coordinates
  let sumLon = 0;
  let sumLat = 0;
  let minLon = Infinity;
  let maxLon = -Infinity;
  let minLat = Infinity;
  let maxLat = -Infinity;
  for (const gcp of gcps) {
    sumLon += gcp.targetX;
    sumLat += gcp.targetY;

    minLon = Math.min(minLon, gcp.targetX);
    maxLon = Math.max(maxLon, gcp.targetX);
    minLat = Math.min(minLat, gcp.targetY);
    maxLat = Math.max(maxLat, gcp.targetY);
  }
  const centerLon = sumLon / gcps.length;
  const centerLat = sumLat / gcps.length;

  // Calculate zoom level based on GCP span
  const lonSpan = maxLon - minLon;
  const latSpan = maxLat - minLat;
  const maxSpan = Math.max(lonSpan, latSpan);
  let zoom = 10;
  if (maxSpan > 5) zoom = 4;
  else if (maxSpan > 2) zoom = 5;
  else if (maxSpan > 1) zoom = 7;
  else if (maxSpan > 0.5) zoom = 8;
  else if (maxSpan > 0.1) zoom = 9;
  else if (maxSpan > 0.05) zoom = 10;
  else if (maxSpan > 0.01) zoom = 11;
  else zoom = 12;

  return {
    longitude: centerLon,
    latitude: centerLat,
    zoom,
  };
};

/**
 * Type definition for Affine transformation parameters
 */
interface AffineParams {
  xScale: number;
  xShear: number;
  xTranslation: number;
  yScale: number;
  yShear: number;
  yTranslation: number;
}

/**
 * Type definition for Residuals
 */
export interface Residual {
  dX: number;
  dY: number;
}

/**
 * Function to calculate Affine transformation parameters from GCPs
 */
export function calculateAffineParams(gcps: GCP[]): AffineParams | null {
  if (gcps.length < 3) {
    return null;
  }

  try {
    const A = gcps.map((gcp) => [gcp.targetX, gcp.targetY, 1]);
    const bX = gcps.map((gcp) => gcp.sourceX);
    const bY = gcps.map((gcp) => gcp.sourceY);

    const aT = math.transpose(A);
    const aTA = math.multiply(aT, A);
    const aTAinv = math.inv(aTA);
    const aTbX = math.multiply(aT, bX);
    const xParams = math.multiply(aTAinv, aTbX);

    const aTbY = math.multiply(aT, bY);
    const yParams = math.multiply(aTAinv, aTbY);

    return {
      xScale: xParams[0],
      xShear: xParams[1],
      xTranslation: xParams[2],
      yShear: yParams[0],
      yScale: yParams[1],
      yTranslation: yParams[2],
    };
  } catch {
    // If the matrix is singular (no inverse exists), return null
    return null;
  }
}

/**
 * Function to transform a point using Affine transformation
 */
function transformPoint(point: { x: number; y: number }, params: AffineParams): { x: number; y: number } {
  return {
    x: params.xScale * point.x + params.xShear * point.y + params.xTranslation,
    y: params.yShear * point.x + params.yScale * point.y + params.yTranslation,
  };
}

/**
 * Function to calculate residuals for GCPs
 */
export function calculateResiduals(gcps: GCP[], params: AffineParams): Map<string, Residual> {
  const residuals = new Map<string, Residual>();

  for (const gcp of gcps) {
    const predicted = transformPoint({ x: gcp.targetX, y: gcp.targetY }, params);

    const dX = gcp.sourceX - predicted.x;
    const dY = gcp.sourceY - predicted.y;

    residuals.set(gcp.id, { dX, dY });
  }

  return residuals;
}
